import React, { useState, useEffect, useContext, useCallback } from 'react';
import api from '../../api';
import Swal from 'sweetalert2';
import { AuthContext } from '../../components/AuthContext';
import { toast } from 'react-toastify';
import { FaPlus, FaEye, FaSearch, FaTimes, FaFilter, FaBox,
  FaClipboardCheck, FaWarehouse, FaCheckCircle, FaClock, FaTruck,
  FaBan, FaCalendarAlt, FaExclamationTriangle } from 'react-icons/fa';

const StockItemsReceived = () => {
  const { user } = useContext(AuthContext);
  const userId = user?.user?._id;
  const companyId = user?.user?.company?._id || user?.user?.company;

  // Dark mode
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');
  useEffect(() => {
    const interval = setInterval(() => {
      setDarkMode(localStorage.getItem('darkMode') === 'true');
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // State
  const [dispatches, setDispatches] = useState([]);
  const [locations, setLocations] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedDispatch, setSelectedDispatch] = useState(null);
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalItems: 0, hasNext: false, hasPrev: false });
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filters, setFilters] = useState({ startDate: '', endDate: '' });

  // Modals
  const [showDispatchModal, setShowDispatchModal] = useState(false);
  const [dispatchSearch, setDispatchSearch] = useState('');
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [locationSearch, setLocationSearch] = useState('');
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);

  // Delivery order commitment tracking
  const [commitments, setCommitments] = useState({});
  const [commitmentsLoading, setCommitmentsLoading] = useState(false);

  const [form, setForm] = useState({
    dispatchId: null,
    location: null,
    items: [],
    receiptDate: new Date().toISOString().split('T')[0],
    remarks: '',
    createdBy: userId
  });

  // ── Data Loading ──
  const fetchDispatches = useCallback(async () => {
    try {
      const response = await api.get(`/stock-items-dispatch/available/for-receiving?company=${companyId}`);
      setDispatches(response.data?.data || []);
    } catch (error) {
      console.error('Failed to fetch dispatches:', error);
    }
  }, [companyId]);

  const fetchLocations = useCallback(async () => {
    try {
      const response = await api.get(`/locations/companyId/${companyId}`);
      setLocations(response.data?.data || response.data || []);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  }, [companyId]);

  const fetchReceipts = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page, limit: 10, company: companyId });
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      
      const response = await api.get(`/stock-items-received?${params}`);
      const { receipts: list, pagination: pag } = response.data?.data || {};
      setReceipts(list || []);
      setPagination(pag || { currentPage: 1, totalPages: 1, totalItems: 0 });
    } catch (error) {
      console.error('Failed to fetch receipts:', error);
    } finally {
      setLoading(false);
    }
  }, [companyId, filters]);

  useEffect(() => { fetchDispatches(); fetchLocations(); }, [fetchDispatches, fetchLocations]);
  useEffect(() => { fetchReceipts(); }, [fetchReceipts]);

  // Fetch delivery order commitments when items and location are set
  const fetchCommitments = async (stockItemIds, locationId) => {
    if (!stockItemIds?.length || !locationId) {
      setCommitments({});
      return;
    }
    try {
      setCommitmentsLoading(true);
      const response = await api.post('/stock-items-received/check-commitments', {
        stockItemIds,
        location: locationId
      });
      if (response.data?.success) {
        setCommitments(response.data.data || {});
      }
    } catch (error) {
      console.error('Error checking commitments:', error);
    } finally {
      setCommitmentsLoading(false);
    }
  };

  // Re-check commitments when location changes and items exist
  useEffect(() => {
    if (form.items.length > 0 && form.location?.value) {
      const stockItemIds = form.items.map(item => item.stockItemId);
      fetchCommitments(stockItemIds, form.location.value);
    } else {
      setCommitments({});
    }
  }, [form.location?.value, form.items.length]);

  // ── Dispatch Selection ──
  const handleDispatchSelect = async (dispatch) => {
    try {
      const response = await api.get(`/stock-items-dispatch/${dispatch._id}`);
      const dispatchData = response.data?.data;
      if (!dispatchData) return toast.error('Failed to load dispatch details');

      const items = dispatchData.items.map(item => {
        const pending = (item.dispatchedQuantity || 0) - (item.receivedQuantity || 0);
        return {
          stockItemId: item.stockItemId?._id || item.stockItemId,
          itemName: item.stockItemId?.name || 'Unknown',
          unitOfMeasure: item.stockItemId?.unitOfMeasure || '',
          dispatchedQuantity: item.dispatchedQuantity || 0,
          receivedQuantity: item.receivedQuantity || 0,
          pendingQuantity: Math.max(0, pending),
          receivedFreshQuantity: 0,
          receivedDamagedQuantity: 0
        };
      }).filter(item => item.pendingQuantity > 0);

      if (items.length === 0) {
        return toast.warning('All items in this dispatch have been received');
      }

      setSelectedDispatch(dispatchData);
      setForm(prev => ({ ...prev, dispatchId: dispatchData._id, items }));
      setShowDispatchModal(false);
      setDispatchSearch('');
    } catch (error) {
      console.error('Dispatch select error:', error);
      toast.error('Failed to load dispatch details');
    }
  };

  // ── Location Selection ──
  const handleLocationSelect = (location) => {
    setForm(prev => ({ ...prev, location: { value: location._id, label: location.name, data: location } }));
    setShowLocationModal(false);
    setLocationSearch('');
  };

  // ── Update Item Fields ──
  const updateItemField = (index, field, value) => {
    setForm(prev => {
      const items = [...prev.items];
      const qty = parseFloat(value) || 0;
      items[index] = { ...items[index], [field]: Math.max(0, qty) };
      
      // Validate: fresh + damaged cannot exceed pending
      const totalReceiving = (items[index].receivedFreshQuantity || 0) + (items[index].receivedDamagedQuantity || 0);
      if (totalReceiving > items[index].pendingQuantity) {
        const excess = totalReceiving - items[index].pendingQuantity;
        items[index][field] = Math.max(0, items[index][field] - excess);
      }
      
      return { ...prev, items };
    });
  };

  // ── Submit ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.dispatchId) return toast.error('Please select a dispatch');
    if (!form.location) return toast.error('Please select a location');
    if (!form.receiptDate) return toast.error('Please enter receipt date');

    const validItems = form.items.filter(i => (i.receivedFreshQuantity || 0) + (i.receivedDamagedQuantity || 0) > 0);
    if (validItems.length === 0) return toast.error('Please enter received quantities for at least one item');

    // Validate quantities
    for (const item of validItems) {
      const total = (item.receivedFreshQuantity || 0) + (item.receivedDamagedQuantity || 0);
      if (total > item.pendingQuantity) {
        return toast.error(`Cannot receive ${total} for "${item.itemName}". Only ${item.pendingQuantity} pending.`);
      }
    }

    // Validate delivery order commitment minimums
    if (Object.keys(commitments).length > 0) {
      const insufficientItems = form.items.filter(item => {
        const commitment = commitments[item.stockItemId];
        return commitment && (item.receivedFreshQuantity || 0) < commitment.minRequiredFresh;
      });

      if (insufficientItems.length > 0) {
        const itemDetails = insufficientItems.map(item => {
          const commitment = commitments[item.stockItemId];
          return `${item.itemName}: Entering ${item.receivedFreshQuantity || 0}, min ${commitment.minRequiredFresh} required${commitment.doNumbers?.length > 0 ? ` (DO: ${commitment.doNumbers.join(', ')})` : ''}`;
        }).join('\n');

        await Swal.fire({
          icon: 'error',
          title: 'Insufficient Fresh Quantity',
          text: insufficientItems.length === 1 ? itemDetails : `Multiple items below minimum:\n${itemDetails}`,
          confirmButtonColor: '#EF4444'
        });
        return;
      }
    }

    try {
      setSubmitting(true);
      const payload = {
        dispatchId: form.dispatchId,
        location: form.location.value,
        items: validItems.map(i => ({
          stockItemId: i.stockItemId,
          orderedQuantity: i.dispatchedQuantity,
          receivedFreshQuantity: i.receivedFreshQuantity || 0,
          receivedDamagedQuantity: i.receivedDamagedQuantity || 0
        })),
        receiptDate: form.receiptDate,
        remarks: form.remarks || undefined,
        createdBy: userId
      };

      await api.post('/stock-items-received', payload);
      toast.success('Items received successfully');
      resetForm();
      fetchDispatches();
      fetchReceipts();
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.response?.data?.message || 'Failed to create receipt');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setForm({
      dispatchId: null, location: null, items: [],
      receiptDate: new Date().toISOString().split('T')[0],
      remarks: '', createdBy: userId
    });
    setSelectedDispatch(null);
  };

  const handleViewReceipt = async (receipt) => {
    try {
      const response = await api.get(`/stock-items-received/${receipt._id}`);
      setSelectedReceipt(response.data?.data);
      setShowViewModal(true);
    } catch (error) {
      toast.error('Failed to load receipt details');
    }
  };

  // ── Status Badge ──
  const getStatusBadge = (status) => {
    const config = {
      'pending': { bg: 'bg-yellow-100 text-yellow-800', icon: <FaClock className="mr-1" />, label: 'Pending' },
      'in-transit': { bg: 'bg-blue-100 text-blue-800', icon: <FaTruck className="mr-1" />, label: 'In Transit' },
      'partial': { bg: 'bg-orange-100 text-orange-800', icon: <FaBox className="mr-1" />, label: 'Partial' },
      'delivered': { bg: 'bg-green-100 text-green-800', icon: <FaCheckCircle className="mr-1" />, label: 'Delivered' },
      'cancelled': { bg: 'bg-red-100 text-red-800', icon: <FaBan className="mr-1" />, label: 'Cancelled' }
    };
    const c = config[status] || { bg: 'bg-gray-100 text-gray-800', label: status };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${c.bg}`}>{c.icon}{c.label}</span>;
  };

  // Filtered lists for modals
  const filteredDispatchesModal = dispatches.filter(d => {
    if (!dispatchSearch.trim()) return true;
    const term = dispatchSearch.toLowerCase();
    return d.dispatchId?.toLowerCase().includes(term) || d.orderId?.orderId?.toLowerCase().includes(term) || d.supplier?.name?.toLowerCase().includes(term);
  });

  const filteredLocationsModal = locations.filter(l => {
    if (!locationSearch.trim()) return true;
    return l.name?.toLowerCase().includes(locationSearch.toLowerCase());
  });

  const cardBg = darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-gray-400' : 'text-gray-600';
  const inputBg = darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900';

  return (
    <div className={`min-h-screen p-4 md:p-6 ${darkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-gray-50 to-gray-100'}`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-6 mb-6 shadow-lg">
        <div className="flex items-center gap-4">
          <div className="bg-white/20 p-3 rounded-xl"><FaClipboardCheck className="text-white w-8 h-8" /></div>
          <div>
            <h1 className="text-2xl font-bold text-white">Stock Items Received</h1>
            <p className="text-white/80 text-sm">Confirm receipt of dispatched stock items at your location</p>
          </div>
        </div>
      </div>

      {/* Receipt Form */}
      <div className={`${cardBg} border rounded-2xl shadow-lg mb-6 overflow-hidden`}>
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <FaPlus className="w-4 h-4" /> New Receipt
          </h2>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Dispatch + Location + Date */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className={`block text-sm font-medium mb-1 ${textSecondary}`}>Select Dispatch *</label>
              <button type="button" onClick={() => setShowDispatchModal(true)}
                className={`w-full text-left px-4 py-3 rounded-xl border-2 border-dashed transition-all ${
                  selectedDispatch
                    ? `${darkMode ? 'border-indigo-500 bg-indigo-900/20' : 'border-indigo-300 bg-indigo-50'}`
                    : `${darkMode ? 'border-gray-600 hover:border-indigo-500' : 'border-gray-300 hover:border-indigo-400'}`
                }`}>
                {selectedDispatch ? (
                  <div className="flex items-center gap-2">
                    <FaCheckCircle className="text-indigo-500 flex-shrink-0" />
                    <div className="min-w-0">
                      <span className={`font-medium ${textPrimary} block truncate`}>{selectedDispatch.dispatchId}</span>
                      <span className={`text-xs ${textSecondary}`}>{selectedDispatch.supplier?.name}</span>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <FaSearch className={textSecondary} />
                    <span className={textSecondary}>Select dispatch</span>
                  </div>
                )}
              </button>
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${textSecondary}`}>Location *</label>
              <button type="button" onClick={() => setShowLocationModal(true)}
                className={`w-full text-left px-4 py-3 rounded-xl border-2 border-dashed transition-all ${
                  form.location
                    ? `${darkMode ? 'border-green-500 bg-green-900/20' : 'border-green-300 bg-green-50'}`
                    : `${darkMode ? 'border-gray-600 hover:border-green-500' : 'border-gray-300 hover:border-green-400'}`
                }`}>
                {form.location ? (
                  <div className="flex items-center gap-2">
                    <FaWarehouse className="text-green-500" />
                    <span className={`font-medium ${textPrimary}`}>{form.location.label}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <FaWarehouse className={textSecondary} />
                    <span className={textSecondary}>Select location</span>
                  </div>
                )}
              </button>
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${textSecondary}`}>Receipt Date *</label>
              <input type="date" value={form.receiptDate}
                onChange={(e) => setForm(prev => ({ ...prev, receiptDate: e.target.value }))}
                className={`w-full px-4 py-3 rounded-xl border ${inputBg} focus:ring-2 focus:ring-indigo-500`} />
            </div>
          </div>

          {/* Dispatch Details Infobox */}
          {selectedDispatch && (
            <div className={`p-3 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-blue-50'} border ${darkMode ? 'border-gray-600' : 'border-blue-200'}`}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                <div><span className={textSecondary}>Order: </span><span className={`font-medium ${textPrimary}`}>{selectedDispatch.orderId?.orderId || 'N/A'}</span></div>
                <div><span className={textSecondary}>Supplier: </span><span className={`font-medium ${textPrimary}`}>{selectedDispatch.supplier?.name || 'N/A'}</span></div>
                <div><span className={textSecondary}>Dispatched: </span><span className={`font-medium ${textPrimary}`}>{new Date(selectedDispatch.dispatchDate || selectedDispatch.createdAt).toLocaleDateString('en-IN')}</span></div>
                <div><span className={textSecondary}>Status: </span>{getStatusBadge(selectedDispatch.status)}</div>
              </div>
            </div>
          )}

          {/* Items Table */}
          {form.items.length > 0 && (
            <div>
              <label className={`block text-sm font-medium mb-2 ${textSecondary}`}>Items to Receive</label>

              {/* Delivery Order Commitment Warning Banner */}
              {Object.keys(commitments).length > 0 && (
                <div className={`mb-3 p-3 rounded-xl border-2 ${darkMode ? 'bg-red-900/30 border-red-700' : 'bg-red-50 border-red-300'}`}>
                  <div className="flex items-start gap-2">
                    <FaExclamationTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <h4 className={`font-bold text-xs mb-1 ${darkMode ? 'text-red-300' : 'text-red-800'}`}>
                        Delivery Order Commitments - Minimum Fresh Qty Required
                      </h4>
                      <div className="space-y-0.5">
                        {Object.entries(commitments).map(([id, info]) => (
                          <div key={id} className={`text-xs ${darkMode ? 'text-red-200' : 'text-red-700'}`}>
                            <strong>{info.itemName}:</strong> Min <span className="font-bold text-red-600">{info.minRequiredFresh}</span>
                            {info.doNumbers?.length > 0 && <span className="opacity-75"> (DO: {info.doNumbers.join(', ')})</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {commitmentsLoading && (
                <div className={`mb-3 p-2 rounded-lg flex items-center gap-2 ${darkMode ? 'bg-gray-600' : 'bg-gray-100'}`}>
                  <div className="w-3 h-3 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                  <span className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Checking delivery order commitments...</span>
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
                      <th className="px-3 py-2 text-left">Item</th>
                      <th className="px-3 py-2 text-center">Dispatched</th>
                      <th className="px-3 py-2 text-center">Already Received</th>
                      <th className="px-3 py-2 text-center">Pending</th>
                      <th className="px-3 py-2 text-center">Fresh Qty</th>
                      <th className="px-3 py-2 text-center">Damaged Qty</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {form.items.map((item, index) => (
                      <tr key={index} className={`${commitments[item.stockItemId] ? (darkMode ? 'bg-red-900/15' : 'bg-red-50/50') : ''} ${darkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'}`}>
                        <td className="px-3 py-2">
                          <div className={`font-medium ${textPrimary}`}>{item.itemName}</div>
                          {item.unitOfMeasure && <div className={`text-xs ${textSecondary}`}>{item.unitOfMeasure}</div>}
                          {commitments[item.stockItemId] && (
                            <div className="mt-1 px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-900/40 border border-red-300 dark:border-red-700 inline-block">
                              <span className="text-xs font-bold text-red-600 dark:text-red-400">
                                Min {commitments[item.stockItemId].minRequiredFresh} fresh required
                              </span>
                            </div>
                          )}
                        </td>
                        <td className={`px-3 py-2 text-center ${textPrimary}`}>{item.dispatchedQuantity}</td>
                        <td className={`px-3 py-2 text-center ${textPrimary}`}>{item.receivedQuantity}</td>
                        <td className="px-3 py-2 text-center">
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">{item.pendingQuantity}</span>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <input type="number" min="0" max={item.pendingQuantity} value={item.receivedFreshQuantity || ''}
                            onChange={(e) => updateItemField(index, 'receivedFreshQuantity', e.target.value)}
                            className={`w-20 mx-auto px-2 py-1.5 rounded-lg border text-center text-sm ${
                              commitments[item.stockItemId] && (item.receivedFreshQuantity || 0) < commitments[item.stockItemId].minRequiredFresh
                                ? 'border-red-500 bg-red-50 dark:bg-red-900/30 ring-1 ring-red-300'
                                : inputBg
                            } focus:ring-2 focus:ring-green-500`}
                            placeholder="0" />
                          {commitments[item.stockItemId] && (item.receivedFreshQuantity || 0) < commitments[item.stockItemId].minRequiredFresh && (
                            <div className="text-xs text-red-600 font-semibold mt-0.5">
                              Min: {commitments[item.stockItemId].minRequiredFresh}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <input type="number" min="0" max={item.pendingQuantity - (item.receivedFreshQuantity || 0)} value={item.receivedDamagedQuantity || ''}
                            onChange={(e) => updateItemField(index, 'receivedDamagedQuantity', e.target.value)}
                            className={`w-20 mx-auto px-2 py-1.5 rounded-lg border text-center text-sm ${inputBg} focus:ring-2 focus:ring-red-500`}
                            placeholder="0" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Warning note */}
              <div className={`mt-3 p-3 rounded-xl ${darkMode ? 'bg-yellow-900/20 border-yellow-700' : 'bg-yellow-50 border-yellow-200'} border flex items-start gap-2`}>
                <FaExclamationTriangle className="text-yellow-500 mt-0.5 flex-shrink-0" />
                <p className={`text-xs ${darkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>
                  Fresh quantity will be added to stock. Damaged quantity will be tracked separately. Total cannot exceed pending.
                </p>
              </div>
            </div>
          )}

          {/* Remarks */}
          <div>
            <label className={`block text-sm font-medium mb-1 ${textSecondary}`}>Remarks</label>
            <textarea value={form.remarks} onChange={(e) => setForm(prev => ({ ...prev, remarks: e.target.value }))}
              className={`w-full px-4 py-3 rounded-xl border ${inputBg} focus:ring-2 focus:ring-indigo-500`}
              rows={2} placeholder="Optional notes..." />
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button type="submit" disabled={submitting}
              className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 rounded-xl font-medium hover:shadow-lg transition-all disabled:opacity-50">
              {submitting ? 'Saving...' : 'Confirm Receipt'}
            </button>
            <button type="button" onClick={resetForm}
              className={`px-6 py-3 rounded-xl border ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'} transition-colors`}>
              Reset
            </button>
          </div>
        </form>
      </div>

      {/* Receipts List */}
      <div className={`${cardBg} border rounded-2xl shadow-lg overflow-hidden`}>
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <FaClipboardCheck className="w-4 h-4" /> Receipt History
          </h2>
          <button onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className="px-3 py-1.5 rounded-lg text-sm text-white/80 hover:text-white hover:bg-white/10 flex items-center gap-1">
            <FaFilter /> Filters
          </button>
        </div>

        {showAdvancedFilters && (
          <div className={`p-4 border-b ${darkMode ? 'border-gray-700 bg-gray-700/50' : 'border-gray-200 bg-gray-50'}`}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className={`text-xs font-medium ${textSecondary}`}>Start Date</label>
                <input type="date" value={filters.startDate}
                  onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                  className={`w-full mt-1 px-3 py-2 rounded-lg border text-sm ${inputBg}`} />
              </div>
              <div>
                <label className={`text-xs font-medium ${textSecondary}`}>End Date</label>
                <input type="date" value={filters.endDate}
                  onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                  className={`w-full mt-1 px-3 py-2 rounded-lg border text-sm ${inputBg}`} />
              </div>
              <div className="flex items-end">
                <button onClick={() => setFilters({ startDate: '', endDate: '' })}
                  className="px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg">
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="p-4">
          {loading ? (
            <div className="text-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto mb-3"></div><p className={textSecondary}>Loading...</p></div>
          ) : receipts.length === 0 ? (
            <div className="text-center py-12">
              <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <FaClipboardCheck className={`w-8 h-8 ${textSecondary}`} />
              </div>
              <h3 className={`text-lg font-medium mb-1 ${textPrimary}`}>No Receipts Yet</h3>
              <p className={textSecondary}>Confirm your first stock items receipt above</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
                      <th className="px-4 py-3 text-left text-sm font-medium">Receipt ID</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">Dispatch</th>
                      <th className="px-4 py-3 text-left text-sm font-medium">Location</th>
                      <th className="px-4 py-3 text-center text-sm font-medium">Items</th>
                      <th className="px-4 py-3 text-center text-sm font-medium">Date</th>
                      <th className="px-4 py-3 text-center text-sm font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {receipts.map((receipt) => (
                      <tr key={receipt._id} className={`${darkMode ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'} transition-colors`}>
                        <td className="px-4 py-3">
                          <span className={`font-medium ${textPrimary}`}>{receipt.receiptId}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-sm ${textPrimary}`}>{receipt.dispatchId?.dispatchId || 'N/A'}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-sm ${textPrimary}`}>{receipt.location?.name || 'N/A'}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${darkMode ? 'bg-indigo-900/30 text-indigo-400' : 'bg-indigo-100 text-indigo-700'}`}>
                            {receipt.items?.length || 0}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`text-sm ${textSecondary}`}>{new Date(receipt.receiptDate || receipt.createdAt).toLocaleDateString('en-IN')}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button onClick={() => handleViewReceipt(receipt)} title="View"
                            className="p-2 rounded-lg text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
                            <FaEye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Pagination */}
              <div className="flex items-center justify-between px-4 py-3 mt-2">
                <span className={`text-sm ${textSecondary}`}>
                  Page {pagination.currentPage} of {pagination.totalPages} ({pagination.totalItems} receipts)
                </span>
                <div className="flex gap-2">
                  <button onClick={() => fetchReceipts(pagination.currentPage - 1)} disabled={!pagination.hasPrev}
                    className={`px-4 py-2 rounded-lg text-sm border ${darkMode ? 'border-gray-600' : 'border-gray-300'} disabled:opacity-40`}>Previous</button>
                  <button onClick={() => fetchReceipts(pagination.currentPage + 1)} disabled={!pagination.hasNext}
                    className={`px-4 py-2 rounded-lg text-sm border ${darkMode ? 'border-gray-600' : 'border-gray-300'} disabled:opacity-40`}>Next</button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Dispatch Selection Modal */}
      {showDispatchModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowDispatchModal(false)}>
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col`} onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className={`text-lg font-semibold ${textPrimary}`}>Select Dispatch</h3>
              <button onClick={() => setShowDispatchModal(false)} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"><FaTimes /></button>
            </div>
            <div className="p-4">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" value={dispatchSearch} onChange={(e) => setDispatchSearch(e.target.value)}
                  placeholder="Search dispatches..." autoFocus
                  className={`w-full pl-10 pr-4 py-2.5 rounded-xl border ${inputBg}`} />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-2">
              {filteredDispatchesModal.length === 0 ? (
                <p className={`text-center py-8 ${textSecondary}`}>No dispatches available for receiving</p>
              ) : filteredDispatchesModal.map(dispatch => (
                <button key={dispatch._id} onClick={() => handleDispatchSelect(dispatch)}
                  className={`w-full text-left p-3 rounded-xl border transition-all ${darkMode ? 'border-gray-700 hover:border-indigo-500' : 'border-gray-200 hover:border-indigo-300'}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className={`font-medium ${textPrimary}`}>{dispatch.dispatchId}</span>
                      <span className={`ml-2 text-sm ${textSecondary}`}>• {dispatch.supplier?.name}</span>
                    </div>
                    {getStatusBadge(dispatch.status)}
                  </div>
                  <div className={`text-xs mt-1 ${textSecondary}`}>
                    Order: {dispatch.orderId?.orderId || 'N/A'} • {dispatch.items?.length} items
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Location Selection Modal */}
      {showLocationModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowLocationModal(false)}>
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col`} onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className={`text-lg font-semibold ${textPrimary}`}>Select Location</h3>
              <button onClick={() => setShowLocationModal(false)} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"><FaTimes /></button>
            </div>
            <div className="p-4">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" value={locationSearch} onChange={(e) => setLocationSearch(e.target.value)}
                  placeholder="Search locations..." autoFocus
                  className={`w-full pl-10 pr-4 py-2.5 rounded-xl border ${inputBg}`} />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-2">
              {filteredLocationsModal.length === 0 ? (
                <p className={`text-center py-8 ${textSecondary}`}>No locations found</p>
              ) : filteredLocationsModal.map(loc => (
                <button key={loc._id} onClick={() => handleLocationSelect(loc)}
                  className={`w-full text-left p-3 rounded-xl border transition-all ${
                    form.location?.value === loc._id
                      ? `${darkMode ? 'border-green-500 bg-green-900/20' : 'border-green-400 bg-green-50'}`
                      : `${darkMode ? 'border-gray-700 hover:border-green-500' : 'border-gray-200 hover:border-green-300'}`
                  }`}>
                  <div className="flex items-center gap-2">
                    <FaWarehouse className={form.location?.value === loc._id ? 'text-green-500' : textSecondary} />
                    <span className={`font-medium ${textPrimary}`}>{loc.name}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* View Receipt Modal */}
      {showViewModal && selectedReceipt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowViewModal(false)}>
          <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col`} onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-4 rounded-t-2xl flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-white">{selectedReceipt.receiptId}</h3>
                <p className="text-white/70 text-sm">Dispatch: {selectedReceipt.dispatchId?.dispatchId || 'N/A'}</p>
              </div>
              <button onClick={() => setShowViewModal(false)} className="p-2 rounded-lg text-white hover:bg-white/20"><FaTimes /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`text-xs ${textSecondary}`}>Location</label>
                  <p className={`font-medium ${textPrimary}`}>{selectedReceipt.location?.name || 'N/A'}</p>
                </div>
                <div>
                  <label className={`text-xs ${textSecondary}`}>Receipt Date</label>
                  <p className={textPrimary}>{new Date(selectedReceipt.receiptDate || selectedReceipt.createdAt).toLocaleDateString('en-IN')}</p>
                </div>
                <div>
                  <label className={`text-xs ${textSecondary}`}>Supplier</label>
                  <p className={`font-medium ${textPrimary}`}>{selectedReceipt.supplier?.name || 'N/A'}</p>
                </div>
                <div>
                  <label className={`text-xs ${textSecondary}`}>Stock Updated</label>
                  <p className={textPrimary}>{selectedReceipt.stockUpdated ? '✅ Yes' : '❌ No'}</p>
                </div>
              </div>

              <div>
                <h4 className={`text-sm font-semibold mb-2 ${textPrimary}`}>Items Received</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className={`${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                        <th className={`px-3 py-2 text-left ${textSecondary}`}>Item</th>
                        <th className={`px-3 py-2 text-center ${textSecondary}`}>Fresh</th>
                        <th className={`px-3 py-2 text-center ${textSecondary}`}>Damaged</th>
                        <th className={`px-3 py-2 text-center ${textSecondary}`}>Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {selectedReceipt.items?.map((item, idx) => (
                        <tr key={idx}>
                          <td className={`px-3 py-2 ${textPrimary}`}>{item.stockItemId?.name || 'N/A'}</td>
                          <td className="px-3 py-2 text-center text-green-600 font-medium">{item.receivedFreshQuantity || 0}</td>
                          <td className="px-3 py-2 text-center text-red-600 font-medium">{item.receivedDamagedQuantity || 0}</td>
                          <td className={`px-3 py-2 text-center font-medium ${textPrimary}`}>{(item.receivedFreshQuantity || 0) + (item.receivedDamagedQuantity || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {selectedReceipt.remarks && (
                <div>
                  <label className={`text-xs ${textSecondary}`}>Remarks</label>
                  <p className={`${textPrimary} text-sm mt-1`}>{selectedReceipt.remarks}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockItemsReceived;
